import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Topic {
  topic_id: number;
  site_id: number;
  topic_name: string;
  is_system: boolean;
  status: boolean;
  created_date: string;
  updated_on: string;
  sub_topics: SubTopic[];
  action?: 'same' | 'create' | 'update' | 'delete';
  originalData?: Topic;
}

export interface SubTopic {
  sub_topic_id: number;
  site_id: number;
  topic_id: number;
  sub_topic_name: string;
  secondary_prompt: string;
  is_system: boolean;
  status: boolean;
  created_date: string;
  updated_on: string;
  action?: 'same' | 'create' | 'update' | 'delete';
  originalData?: SubTopic;
}

export interface TopicsResponse {
  success: boolean;
  data: Topic[];
  message: string;
}

export interface BatchOperationsPayload {
  site_id: number;
  operations: {
    delete?: {
      topics?: number[];
      subTopics?: number[];
    };
    update?: {
      topics?: Array<{
        topic_id: number;
        topic_name: string;
      }>;
      subTopics?: Array<{
        sub_topic_id: number;
        sub_topic_name: string;
        secondary_prompt: string;
      }>;
    };
    create?: {
      topics?: Array<{
        topic_name: string;
        sub_topics?: Array<{
          sub_topic_name: string;
          secondary_prompt: string;
        }>;
      }>;
      subTopics?: Array<{
        topic_id: number;
        sub_topic_name: string;
        secondary_prompt: string;
      }>;
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class AiTopicSettingsService {
  private baseUrl = environment.nodeUrl1;

  constructor(private http: HttpClient) { }

  getTopicsWithSubtopics(userId: number, siteId?: number): Observable<TopicsResponse> {
    let url = `${this.baseUrl}api/topics-with-subtopics`;
    let params: any = {};

    if (userId === 1) {
      // System user - use is_system=true, no site_id
      params.is_system = 'true';
    } else if (siteId) {
      // Regular user - use site_id, no is_system
      params.site_id = siteId.toString();
    }

    // Build query string
    const queryString = Object.keys(params)
      .map(key => `${key}=${params[key]}`)
      .join('&');

    if (queryString) {
      url += `?${queryString}`;
    }

    return this.http.get<TopicsResponse>(url);
  }

  getAllTopics(userId: number, siteId?: number): Observable<any> {
    let url = `${this.baseUrl}api/topics`;
    let params: any = {};

    if (userId === 1) {
      // System user - use is_system=true, no site_id
      params.is_system = 'true';
    } else if (siteId) {
      // Regular user - use site_id, no is_system
      params.site_id = siteId.toString();
    }

    // Build query string
    const queryString = Object.keys(params)
      .map(key => `${key}=${params[key]}`)
      .join('&');

    if (queryString) {
      url += `?${queryString}`;
    }

    return this.http.get(url);
  }

  addTopic(topicData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}api/topics`, topicData);
  }

  addSubTopic(subTopicData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}api/subtopics`, subTopicData);
  }

  updateTopic(topicId: number, topicData: any): Observable<any> {
    return this.http.put(`${this.baseUrl}api/topics/${topicId}`, topicData);
  }

  updateSubTopic(subTopicId: number, subTopicData: any): Observable<any> {
    return this.http.put(`${this.baseUrl}api/subtopics/${subTopicId}`, subTopicData);
  }

  deleteTopic(topicId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}api/topics/${topicId}`);
  }

  deleteSubTopic(subTopicId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}api/subtopics/${subTopicId}`);
  }

  copySystemTopics(siteId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}api/copy-system-topics`, { site_id: siteId });
  }

  batchOperations(payload: BatchOperationsPayload): Observable<any> {
    return this.http.post(`${this.baseUrl}api/batch-operations`, payload);
  }
}
